/**
 * Body-portaled SVG annotation lightbox.
 *
 * Renders one large, contained view of the full source image in viewport space.
 * The portal is deliberately owned here rather than copied into every node
 * family, and is classified in the canvas blocking-overlay inventory.
 *
 * `useBlockingOverlay(true)` is nonetheless mandatory and orthogonal: without
 * it, the canvas keydown handlers in `WorkspacePageV2` and `GroupNode` would
 * delete the UNDERLYING NODE when the user presses Delete to remove a selected
 * annotation. That is the single most damaging failure this component can have,
 * and it is asserted by the demo spec rather than left to review.
 *
 * The editor draws its own `<img>` rather than reaching into the host node's:
 * it needs the intrinsic dimensions, and owning the element keeps node
 * integration thin across all supported node families. The surface shrink-wraps
 * an `object-contain` image so the full bake source is visible without cropping.
 */

import {
  forwardRef,
  useImperativeHandle,
  createElement,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { useBlockingOverlay } from '../../../../../hooks/canvasShortcutGuard'
import { createNadDebug } from '../../../../../utils/debugLog'
import type { ImageAnnotationDict } from '../../../../../generated/nodeDataTypes'
import {
  ANNOTATION_FONT_FAMILY,
  ANNOTATION_FONT_WEIGHT,
  MAX_ANNOTATIONS,
  buildAnnotationPrimitives,
  effectiveFontPx,
} from './annotationGeometry'
import { primitiveToSvgElement } from './annotationSvg'
import { ensureAnnotationFontLoaded } from './annotationFont'
import { hitTestAnnotations } from './annotationHitTest'
import {
  measureAnnotationSelectionBounds,
  type AnnotationSelectionBounds,
} from './annotationSelectionBounds'
import {
  canRedo as historyCanRedo,
  canUndo as historyCanUndo,
  commit,
  createHistory,
  makeElementId,
  redo as historyRedo,
  removeElement,
  replaceElement,
  translateElement,
  undo as historyUndo,
} from './annotationModel'
import {
  DEFAULT_ANNOTATION_COLOR,
  DEFAULT_FONT_SIZE_PCT,
  DEFAULT_STROKE_WIDTH_PCT,
  FONT_SIZE_STEP_PCT,
  MAX_FONT_SIZE_PCT,
  MIN_FONT_SIZE_PCT,
  MIN_PEN_POINT_SPACING_PCT,
  MIN_SHAPE_TRAVEL_PCT,
  type AnnotationTool,
} from './annotationTools'
import { AnnotationToolPalette } from './AnnotationToolPalette'
import { displayScale, percentToLocal, pointerToPercent } from './coordinateMapping'
import './annotationFont.css'

const editorLog = createNadDebug('dw:image-annotation:editor')
const FEATURE_TAG = 'image-annotation'

interface Naturals {
  width: number
  height: number
}

interface TextDraft {
  xPct: number
  yPct: number
  value: string
}

export interface AnnotationEditorOverlayProps {
  /** Full-resolution image URL — the same asset the bake will read. */
  imageUrl: string
  altText: string
  /** Correlates every editor event with the backend's `annot_<hex8>` chain. */
  traceId: string
  nodeId: string
  isSaving: boolean
  onCancel: () => void
  onSave: (annotations: ImageAnnotationDict[]) => void
  /**
   * `lightbox` (default) is the body-portaled viewport overlay launched from
   * the node toolbar. `inline` renders the same surface inside a host panel —
   * the Canvas UI 2.0 Refine tab — with no portal, no backdrop, and no commit
   * buttons, because there the panel's Generate button is the action.
   *
   * The geometry needs no adaptation between the two: annotations are stored
   * as percentages of the source image and `coordinateMapping` already does
   * the full `object-contain` maths, so a smaller surface maps identically.
   */
  presentation?: 'lightbox' | 'inline'
}

/** Imperative surface for `inline` hosts, which have no Save button. */
export interface AnnotationEditorHandle {
  /**
   * Current annotations, or null when there is nothing to bake.
   *
   * Shares the folding rule with the Save button rather than reading raw
   * state: an open, uncommitted text label is visible on screen, so omitting
   * it would bake something that disagrees with the preview.
   */
  collectAnnotations: () => ImageAnnotationDict[] | null
}

export const AnnotationEditorOverlay = memo(forwardRef<
  AnnotationEditorHandle,
  AnnotationEditorOverlayProps
>(function AnnotationEditorOverlay({
  imageUrl,
  altText,
  traceId,
  nodeId,
  isSaving,
  onCancel,
  onSave,
  presentation = 'lightbox',
}, ref) {
  const { t } = useTranslation('canvas')
  const isInline = presentation === 'inline'

  const rootRef = useRef<HTMLDivElement | null>(null)
  const surfaceRef = useRef<HTMLDivElement | null>(null)
  const svgRef = useRef<SVGSVGElement | null>(null)
  const textInputRef = useRef<HTMLInputElement | null>(null)
  const sequenceRef = useRef(0)
  const draggingRef = useRef<{
    id: string
    lastX: number
    lastY: number
    /** Pre-drag document, so the whole gesture collapses into one undo step. */
    snapshot: ImageAnnotationDict[]
  } | null>(null)

  const [naturals, setNaturals] = useState<Naturals | null>(null)
  const [history, setHistory] = useState(() => createHistory())
  const [draft, setDraft] = useState<ImageAnnotationDict | null>(null)
  const [textDraft, setTextDraft] = useState<TextDraft | null>(null)
  const [tool, setTool] = useState<AnnotationTool>('pen')
  const [color, setColor] = useState(DEFAULT_ANNOTATION_COLOR)
  const [strokeWidth, setStrokeWidth] = useState(DEFAULT_STROKE_WIDTH_PCT)
  const [fontSize, setFontSize] = useState(DEFAULT_FONT_SIZE_PCT)
  const [fontReady, setFontReady] = useState(false)
  const [fontError, setFontError] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  // Stand the canvas keydown handlers down while the editor owns the keyboard,
  // or Delete removes the UNDERLYING NODE instead of the selected annotation —
  // the single most damaging failure this component can have.
  //
  // The lightbox covers the canvas, so it always blocks. The inline surface
  // does NOT: it lives in the Refine panel, which a user can leave open
  // indefinitely, and blocking for that whole time would silently disable
  // canvas Delete / Cmd+Z. It therefore blocks only while actually engaged —
  // a drawing tool active, an annotation selected, or a text label open.
  const editorOwnsKeyboard =
    !isInline || tool !== 'select' || selectedId !== null || textDraft !== null
  useBlockingOverlay(editorOwnsKeyboard)
  const [selectedBox, setSelectedBox] = useState<AnnotationSelectionBounds | null>(null)
  const [limitReached, setLimitReached] = useState(false)

  const elements = history.present

  useEffect(() => {
    let active = true
    void ensureAnnotationFontLoaded()
      .then(() => {
        if (!active) return
        setFontReady(true)
        setFontError(null)
        editorLog({
          event: 'annotate:font_loaded',
          feature: FEATURE_TAG,
          concern: 'font',
          trace_id: traceId,
          node_id: nodeId,
          font_family: ANNOTATION_FONT_FAMILY,
          scripts: ['latin', 'hangul', 'han'],
        })
      })
      .catch((error) => {
        if (!active) return
        const message = error instanceof Error ? error.message : String(error)
        setFontReady(false)
        setFontError(message)
        editorLog({
          event: 'annotate:font_load_failed',
          feature: FEATURE_TAG,
          concern: 'font',
          trace_id: traceId,
          node_id: nodeId,
          reason: message,
        })
      })
    return () => {
      active = false
    }
  }, [nodeId, traceId])

  // ── Geometry helpers ───────────────────────────────────────────────────────

  const measure = useCallback(() => {
    const surface = surfaceRef.current
    if (!surface || !naturals) return null
    const rect = surface.getBoundingClientRect()
    return {
      rectLeft: rect.left,
      rectTop: rect.top,
      rectWidth: rect.width,
      offsetWidth: surface.offsetWidth,
      offsetHeight: surface.offsetHeight,
      naturalWidth: naturals.width,
      naturalHeight: naturals.height,
    }
  }, [naturals])

  const toPercent = useCallback(
    (clientX: number, clientY: number) => {
      const box = measure()
      if (!box) return null
      return pointerToPercent({ ...box, clientX, clientY })
    },
    [measure],
  )

  // ── Commit helpers ─────────────────────────────────────────────────────────

  const commitElements = useCallback((next: ImageAnnotationDict[]) => {
    setHistory((current) => commit(current, next))
  }, [])

  const commitElement = useCallback(
    (element: ImageAnnotationDict) => {
      // Deliberately NOT inside a state updater: updaters must be pure, and
      // React StrictMode double-invokes them in development — a log or a toast
      // in there fires twice, and a counter bump would corrupt the spec.
      if (elements.length >= MAX_ANNOTATIONS) {
        setLimitReached(true)
        return
      }
      setLimitReached(false)
      commitElements([...elements, element])
      editorLog({
        event: 'annotate:element_committed',
        concern: 'editor',
        trace_id: traceId,
        node_id: nodeId,
        kind: element.kind,
        element_count: elements.length + 1,
        point_count: element.points.length,
        stroke_width_pct: element.stroke_width,
        color: element.color,
      })
    },
    [commitElements, elements, nodeId, traceId],
  )

  const nextId = useCallback((kind: string) => {
    sequenceRef.current += 1
    return makeElementId(kind, sequenceRef.current)
  }, [])

  // ── Text ───────────────────────────────────────────────────────────────────

  /**
   * The element an open, non-empty text draft would become — or null.
   *
   * Separate from `commitTextDraft` because Save needs the VALUE, not a state
   * update: committing through `setState` and then reading `elements` in the
   * same handler reads the pre-update array, which silently dropped a label the
   * user could see on screen from the baked image.
   */
  const pendingTextElement = useCallback((): ImageAnnotationDict | null => {
    if (!textDraft) return null
    const text = textDraft.value.trim()
    if (text === '') return null
    return {
      id: nextId('text'),
      kind: 'text',
      color,
      stroke_width: strokeWidth,
      font_size: fontSize,
      text,
      points: [{ x: textDraft.xPct, y: textDraft.yPct }],
    }
  }, [color, fontSize, nextId, strokeWidth, textDraft])

  const commitTextDraft = useCallback(() => {
    if (!textDraft) return
    const element = pendingTextElement()
    setTextDraft(null)
    if (element) commitElement(element)
    editorLog({
      event: 'annotate:text_draft_committed',
      concern: 'text-draft',
      trace_id: traceId,
      node_id: nodeId,
      committed: element !== null,
      char_count: textDraft.value.trim().length,
    })
  }, [commitElement, nodeId, pendingTextElement, textDraft, traceId])

  // ── Pointer handling ───────────────────────────────────────────────────────

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      // Keep ReactFlow from starting a node drag or a rubber-band selection.
      event.stopPropagation()
      if (isSaving) return
      const point = toPercent(event.clientX, event.clientY)
      if (!point || !naturals) return

      // Pointer capture is what keeps a drag alive once the cursor leaves the
      // node, which on a small card is most drags.
      event.currentTarget.setPointerCapture(event.pointerId)

      // Clicking away from an open label commits it. This is why the input has
      // no onBlur handler: blur fires for reasons that are not user intent
      // (ReactFlow moving focus after pointerup, for one), and committing there
      // tore the field down again a frame after it appeared.
      if (textDraft) {
        commitTextDraft()
        if (tool === 'text') return
      }

      if (tool === 'select') {
        const box = measure()
        const scale = box ? displayScale(box) : 1
        const hit = hitTestAnnotations({
          point: {
            x: (point.x / 100) * naturals.width,
            y: (point.y / 100) * naturals.height,
          },
          elements,
          width: naturals.width,
          height: naturals.height,
          scale,
        })
        setSelectedId(hit)
        if (hit !== null) {
          // SHORTCUT_ID: annotate.moveElement
          const drag = {
            id: hit,
            lastX: point.x,
            lastY: point.y,
            snapshot: elements,
          }
          draggingRef.current = drag
        }
        return
      }

      if (tool === 'text') {
        if (!fontReady) {
          editorLog({
            event: 'annotate:text_start_blocked',
            feature: FEATURE_TAG,
            concern: 'font',
            trace_id: traceId,
            node_id: nodeId,
            reason: fontError ?? 'font-loading',
          })
          return
        }
        // Refuse at the cap rather than letting a draft be typed that Save
        // would then have to drop or reject. Shapes hit the same ceiling when
        // they commit; text has to check earlier because its draft is authored
        // before it becomes an element.
        if (elements.length >= MAX_ANNOTATIONS) {
          setLimitReached(true)
          return
        }
        setTextDraft({ xPct: point.x, yPct: point.y, value: '' })
        editorLog({
          event: 'annotate:text_draft_started',
          concern: 'text-draft',
          trace_id: traceId,
          node_id: nodeId,
          x_pct: point.x,
          y_pct: point.y,
        })
        return
      }

      const kind = tool === 'pen' ? 'stroke' : tool
      setSelectedId(null)
      setDraft({
        id: nextId(kind),
        kind,
        color,
        stroke_width: strokeWidth,
        points: tool === 'pen' ? [point] : [point, point],
      })
    },
    [
      color,
      commitTextDraft,
      elements,
      fontError,
      fontReady,
      isSaving,
      measure,
      naturals,
      nextId,
      nodeId,
      strokeWidth,
      textDraft,
      toPercent,
      tool,
      traceId,
    ],
  )

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      // Once the save is in flight the spec is frozen; continuing to mutate it
      // would leave the editor showing something the bake does not contain.
      if (isSaving) return
      const point = toPercent(event.clientX, event.clientY)
      if (!point) return

      const drag = draggingRef.current
      if (drag) {
        const deltaX = point.x - drag.lastX
        const deltaY = point.y - drag.lastY
        drag.lastX = point.x
        drag.lastY = point.y
        setHistory((current) => {
          const target = current.present.find((element) => element.id === drag.id)
          if (!target) return current
          // Live drag mutates `present` without pushing history — the whole drag
          // becomes one undo step when the pointer is released.
          return {
            ...current,
            present: replaceElement(current.present, translateElement(target, deltaX, deltaY)),
          }
        })
        return
      }

      setDraft((current) => {
        if (!current) return current
        if (current.kind === 'stroke') {
          const last = current.points[current.points.length - 1]
          const travelled = Math.hypot(point.x - last.x, point.y - last.y)
          if (travelled < MIN_PEN_POINT_SPACING_PCT) return current
          return { ...current, points: [...current.points, point] }
        }
        return { ...current, points: [current.points[0], point] }
      })
    },
    [isSaving, toPercent],
  )

  /**
   * Pull keyboard focus into the editor after a gesture on the surface.
   *
   * The inline keydown handler only answers keys whose target is inside the
   * editor (so Backspace typed in the Refine prompt cannot delete a drawing).
   * But the surface is not focusable, so a click on it left focus on the
   * nearest focusable ancestor — the ReactFlow node wrapper, OUTSIDE the
   * editor — and ReactFlow re-focuses that wrapper on pointerup anyway. Every
   * real Backspace/Delete/Cmd+Z after selecting a drawing was then discarded
   * as "not for us" (T292732842).
   *
   * Deferred a frame so it lands after ReactFlow's own pointerup focus, and
   * skipped when focus is already inside the editor — the text label's input
   * focuses itself the same way and must keep it.
   */
  const claimKeyboardFocus = useCallback(() => {
    if (!isInline) return
    requestAnimationFrame(() => {
      const root = rootRef.current
      if (!root) return
      const active = document.activeElement
      if (active && root.contains(active)) return
      root.focus({ preventScroll: true })
      editorLog({
        event: 'annotate:keyboard_focus_claimed',
        concern: 'editor',
        trace_id: traceId,
        node_id: nodeId,
        from_tag: active?.tagName ?? null,
        from_testid: active instanceof HTMLElement ? active.dataset.testid ?? null : null,
      })
    })
  }, [isInline, nodeId, traceId])

  const handlePointerUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId)
      }
      if (isSaving) return
      claimKeyboardFocus()

      const drag = draggingRef.current
      if (drag) {
        draggingRef.current = null
        // Close the whole drag as ONE undo step: pointermove mutated `present`
        // in place, so re-seat the pre-drag snapshot as the undoable state.
        // Pure, and a no-op when the pointer never actually moved (a plain
        // click to select must not push a history entry).
        setHistory((current) =>
          current.present === drag.snapshot
            ? current
            : commit({ ...current, present: drag.snapshot }, current.present),
        )
        return
      }

      // Read `draft` from render scope rather than from a setState updater —
      // committing inside an updater would double-fire under StrictMode.
      if (draft) {
        setDraft(null)
        if (draft.kind === 'stroke') {
          if (draft.points.length >= 2) commitElement(draft)
        } else {
          const [start, end] = draft.points
          const travelled = Math.hypot(end.x - start.x, end.y - start.y)
          // A click with a shape tool selected is not a zero-size shape.
          if (travelled >= MIN_SHAPE_TRAVEL_PCT) commitElement(draft)
        }
      }
    },
    [claimKeyboardFocus, commitElement, draft, isSaving],
  )

  // ── History / delete ───────────────────────────────────────────────────────

  const canUndo = historyCanUndo(history)
  const canRedo = historyCanRedo(history)

  const handleUndo = useCallback(() => {
    setHistory((current) => {
      const next = historyUndo(current)
      editorLog({
        event: 'annotate:history',
        concern: 'editor',
        trace_id: traceId,
        op: 'undo',
        past_depth: next.past.length,
        future_depth: next.future.length,
        element_count: next.present.length,
      })
      return next
    })
    setSelectedId(null)
  }, [traceId])

  const handleRedo = useCallback(() => {
    setHistory((current) => {
      const next = historyRedo(current)
      editorLog({
        event: 'annotate:history',
        concern: 'editor',
        trace_id: traceId,
        op: 'redo',
        past_depth: next.past.length,
        future_depth: next.future.length,
        element_count: next.present.length,
      })
      return next
    })
    setSelectedId(null)
  }, [traceId])

  const handleDeleteSelected = useCallback(() => {
    if (selectedId === null) return
    const target = elements.find((element) => element.id === selectedId)
    commitElements(removeElement(elements, selectedId))
    setSelectedId(null)
    // Deleting drops us back under the cap, so the banner must stop claiming
    // otherwise — it has no other path back to false.
    setLimitReached(false)
    editorLog({
      event: 'annotate:element_deleted',
      concern: 'editor',
      trace_id: traceId,
      kind: target?.kind ?? 'unknown',
      element_count: Math.max(0, elements.length - 1),
    })
  }, [commitElements, elements, selectedId, traceId])

  const selectedElement = useMemo(
    () => elements.find((element) => element.id === selectedId) ?? null,
    [elements, selectedId],
  )
  const widthControlsText =
    selectedElement?.kind === 'text' || (!selectedElement && tool === 'text')
  const widthValue = widthControlsText
    ? (selectedElement?.font_size ?? fontSize)
    : (selectedElement?.stroke_width ?? strokeWidth)

  const handleWidthChange = useCallback(
    (nextWidth: number) => {
      if (selectedElement) {
        const nextElement =
          selectedElement.kind === 'text'
            ? { ...selectedElement, font_size: nextWidth }
            : { ...selectedElement, stroke_width: nextWidth }
        commitElements(replaceElement(elements, nextElement))
        if (selectedElement.kind === 'text') setFontSize(nextWidth)
        else setStrokeWidth(nextWidth)
        editorLog({
          event: 'annotate:selected_width_changed',
          feature: FEATURE_TAG,
          concern: 'editor',
          trace_id: traceId,
          node_id: nodeId,
          element_id: selectedElement.id,
          kind: selectedElement.kind,
          width_pct: nextWidth,
          width_field: selectedElement.kind === 'text' ? 'font_size' : 'stroke_width',
        })
        return
      }

      if (tool === 'text') setFontSize(nextWidth)
      else setStrokeWidth(nextWidth)
      editorLog({
        event: 'annotate:width_changed',
        feature: FEATURE_TAG,
        concern: 'editor',
        trace_id: traceId,
        node_id: nodeId,
        tool,
        width_pct: nextWidth,
        width_field: tool === 'text' ? 'font_size' : 'stroke_width',
      })
    },
    [commitElements, elements, nodeId, selectedElement, tool, traceId],
  )

  /**
   * The elements a bake should receive, or null when there is nothing to bake.
   *
   * Folds an open text draft in rather than dropping it: the user can see the
   * label on screen, so leaving it out would bake something that silently
   * disagrees with the preview. Shared by the Save button and the inline
   * host's imperative `collectAnnotations`, so the two cannot diverge.
   */
  const collectFinalElements = useCallback((): ImageAnnotationDict[] | null => {
    const pending = pendingTextElement()
    if (pending && elements.length >= MAX_ANNOTATIONS) {
      setLimitReached(true)
      return null
    }
    const finalElements = pending ? [...elements, pending] : elements
    if (finalElements.length === 0) return null
    if (pending) {
      setTextDraft(null)
      commitElements(finalElements)
    }
    return finalElements
  }, [commitElements, elements, pendingTextElement])

  useImperativeHandle(ref, () => ({ collectAnnotations: collectFinalElements }), [
    collectFinalElements,
  ])

  const handleSave = useCallback(() => {
    if (isSaving) return
    const pending = pendingTextElement()
    if (pending && elements.length >= MAX_ANNOTATIONS) {
      // Belt and braces with the check above: appending here would post
      // MAX_ANNOTATIONS + 1 and earn a 422 from a save the user had every
      // reason to expect to work. Surface the limit instead of failing at the
      // boundary.
      setLimitReached(true)
      return
    }
    const finalElements = pending ? [...elements, pending] : elements
    if (finalElements.length === 0) return
    if (pending) {
      setTextDraft(null)
      commitElements(finalElements)
    }
    editorLog({
      event: 'annotate:editor_save_submitted',
      concern: 'editor',
      trace_id: traceId,
      node_id: nodeId,
      element_count: finalElements.length,
      included_active_text_draft: pending !== null,
    })
    onSave(finalElements)
  }, [commitElements, elements, isSaving, nodeId, onSave, pendingTextElement, traceId])

  // ── Keyboard ───────────────────────────────────────────────────────────────

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      // The lightbox is modal and owns the document, so it keeps answering
      // every key. Inline does NOT: it is a region inside the Refine panel,
      // sitting beside the prompt, and a window listener that fires for keys
      // typed anywhere on the page meant Backspace in the prompt deleted the
      // selected annotation and Cmd+Z undid the drawing instead of the text.
      // Escape was swallowed page-wide too, since it is handled above the
      // old text-field guard.
      if (isInline) {
        const node = event.target instanceof Node ? event.target : null
        if (!node || !rootRef.current?.contains(node)) return
      }
      if (event.key === 'Escape') {
        // Inline is not modal: with no text label open there is nothing for
        // Escape to protect, so let it reach the panel/canvas. This matters
        // now that a click on the surface pulls focus into the editor
        // (`claimKeyboardFocus`) — without this, Escape would be swallowed
        // for as long as the Refine editor held focus.
        if (isInline && textDraft === null) return
        // Explicit-exit contract: Escape must not close the lightbox or discard
        // an active text draft. Keep it from reaching any other modal/canvas
        // handler and record the ignored attempt for NAD verification.
        event.preventDefault()
        event.stopImmediatePropagation()
        if (!event.repeat) {
          editorLog({
            event: 'annotate:escape_ignored',
            concern: 'editor',
            trace_id: traceId,
            node_id: nodeId,
          })
        }
        return
      }
      const target = event.target
      // Never steal keys from a text field. `isContentEditable` matters as much
      // as the two input elements — the canvas prompt is a contentEditable div
      // (RichTextBox), not a <textarea>, so an instanceof-only check misses it.
      //
      // Belt-and-braces only: the inline containment check above already stops
      // the prompt's keys reaching here, and jsdom does not implement
      // `isContentEditable`, so no test exercises this branch. It stays because
      // the guard's stated intent is "not a text field" and an instanceof pair
      // cannot express that.
      if (
        target instanceof HTMLElement &&
        (target.isContentEditable ||
          target instanceof HTMLInputElement ||
          target instanceof HTMLTextAreaElement)
      ) {
        return
      }
      // SHORTCUT_ID: annotate.redo
      if (event.key.toLowerCase() === 'z' && (event.metaKey || event.ctrlKey) && event.shiftKey) {
        event.preventDefault()
        handleRedo()
        return
      }
      // SHORTCUT_ID: annotate.undo
      if (event.key.toLowerCase() === 'z' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault()
        handleUndo()
        return
      }
      // SHORTCUT_ID: annotate.deleteElement
      if (event.key === 'Delete' || event.key === 'Backspace') {
        if (selectedId === null) return
        event.preventDefault()
        handleDeleteSelected()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [handleDeleteSelected, handleRedo, handleUndo, isInline, nodeId, selectedId, textDraft, traceId])

  // ── Render ─────────────────────────────────────────────────────────────────

  const renderable = useMemo(() => {
    if (!naturals) return []
    const all = draft ? [...elements, draft] : elements
    // A malformed in-progress element must not blank the whole overlay.
    try {
      return buildAnnotationPrimitives(all, naturals.width, naturals.height)
    } catch {
      return buildAnnotationPrimitives(elements, naturals.width, naturals.height)
    }
  }, [draft, elements, naturals])

  useLayoutEffect(() => {
    if (!naturals || selectedId === null) {
      setSelectedBox(null)
      return
    }
    const padding = Math.max(naturals.width, naturals.height) * 0.012
    const next = measureAnnotationSelectionBounds(svgRef.current, renderable, selectedId, padding)
    setSelectedBox(next)
    if (next) {
      editorLog({
        event: 'annotate:selection_measured',
        feature: FEATURE_TAG,
        concern: 'selection',
        trace_id: traceId,
        node_id: nodeId,
        element_id: selectedId,
        width_px: next.width,
        height_px: next.height,
        font_ready: fontReady,
      })
    }
  }, [fontReady, naturals, nodeId, renderable, selectedId, traceId])

  const textDraftVisual = useMemo(() => {
    const surface = surfaceRef.current
    if (!textDraft || !naturals || !surface) return null
    const position = percentToLocal({
      xPct: textDraft.xPct,
      yPct: textDraft.yPct,
      offsetWidth: surface.offsetWidth,
      offsetHeight: surface.offsetHeight,
      naturalWidth: naturals.width,
      naturalHeight: naturals.height,
    })
    const box = measure()
    const scale = box ? displayScale(box) : 1
    const renderedFontSize = effectiveFontPx(fontSize, naturals.height) * scale
    const minWidth = Math.max(48, renderedFontSize * 2)
    const contentWidth = Math.max(minWidth, (textDraft.value.length + 1) * renderedFontSize * 0.68)
    return {
      ...position,
      fontSize: renderedFontSize,
      minWidth,
      contentWidth,
    }
  }, [fontSize, measure, naturals, textDraft])

  const body = (
    <div
      ref={rootRef}
      data-testid="annotation-editor"
      data-font-ready={fontReady}
      data-overlay-kind="image-annotation"
      data-presentation={presentation}
      // Inline is a region inside the Refine panel, not a modal: claiming
      // `dialog`/`aria-modal` there would tell a screen reader the rest of the
      // page is inert while it plainly is not.
      role={isInline ? 'group' : 'dialog'}
      aria-modal={isInline ? undefined : true}
      aria-label={t('annotate.button')}
      // Programmatically focusable (not a tab stop) so keyboard shortcuts have
      // a target inside the editor — see `claimKeyboardFocus`.
      tabIndex={isInline ? -1 : undefined}
      className={
        isInline
          ? 'nodrag nopan nowheel relative flex h-full w-full flex-col items-stretch gap-1.5 overflow-hidden outline-none'
          : 'nodrag nopan fixed inset-0 z-[10000] flex items-center justify-center bg-background/90 p-8 pt-20 backdrop-blur-sm'
      }
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => {
        event.stopPropagation()
        if (event.target !== event.currentTarget) return
        editorLog({
          event: 'annotate:backdrop_click_ignored',
          concern: 'editor',
          trace_id: traceId,
          node_id: nodeId,
        })
      }}
    >
      <div
        ref={surfaceRef}
        data-testid="annotation-surface"
        className={
          isInline
            ? 'relative inline-block min-h-0 max-h-full max-w-full touch-none self-center overflow-hidden rounded-lg border border-border-subtle'
            : 'relative inline-block max-h-[calc(100vh-7rem)] max-w-[calc(100vw-4rem)] touch-none overflow-hidden rounded-xl border border-border-subtle shadow-2xl'
        }
        style={{ cursor: tool === 'select' ? 'default' : 'crosshair' }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <img
          src={imageUrl}
          alt={altText}
          draggable={false}
          className={
            isInline
              ? 'block max-h-full max-w-full select-none object-contain'
              : 'block max-h-[calc(100vh-7rem)] max-w-[calc(100vw-4rem)] select-none object-contain'
          }
          onLoad={(event) => {
            const { naturalWidth, naturalHeight } = event.currentTarget
            setNaturals({ width: naturalWidth, height: naturalHeight })
            editorLog({
              event: 'annotate:editor_opened',
              concern: 'editor',
              trace_id: traceId,
              node_id: nodeId,
              natural_w: naturalWidth,
              natural_h: naturalHeight,
              box_w: surfaceRef.current?.offsetWidth ?? 0,
              box_h: surfaceRef.current?.offsetHeight ?? 0,
              view_mode: 'contained-lightbox',
            })
          }}
        />

        {naturals && (
          <svg
            ref={svgRef}
            data-testid="annotation-svg"
            className="pointer-events-none absolute inset-0 h-full w-full"
            viewBox={`0 0 ${naturals.width} ${naturals.height}`}
            /* `meet` is the SVG spelling of `object-contain`, so the full
               source remains visible and percent maps directly to bake space. */
            preserveAspectRatio="xMidYMid meet"
          >
            {renderable.map((primitive, index) => {
              const spec = primitiveToSvgElement(primitive)
              return createElement(
                spec.tag,
                {
                  ...spec.attrs,
                  key: `${primitive.id}-${index}`,
                  'data-annotation-id': primitive.id,
                },
                spec.textContent,
              )
            })}
            {selectedBox && (
              <rect
                data-testid="annotation-selection"
                x={selectedBox.x}
                y={selectedBox.y}
                width={selectedBox.width}
                height={selectedBox.height}
                fill="none"
                stroke="currentColor"
                className="text-text-primary"
                strokeWidth={Math.max(1, naturals.width * 0.003)}
                strokeDasharray={`${naturals.width * 0.012} ${naturals.width * 0.008}`}
              />
            )}
          </svg>
        )}

        {textDraft && textDraftVisual && fontReady && (
          <input
            type="text"
            /* A callback ref rather than `autoFocus`: React's autoFocus runs
               during commit, and ReactFlow moves focus back to the node wrapper
               on the pointerup that follows the placing click — so the field
               appeared but keystrokes went to the window. Focusing in a
               requestAnimationFrame lands after that. */
            ref={(element) => {
              textInputRef.current = element
              if (element) requestAnimationFrame(() => element.focus())
            }}
            data-testid="annotation-text-input"
            aria-label={t('annotate.textPlaceholder')}
            className="nodrag nopan absolute z-10 border-0 bg-transparent p-0 outline-none ring-0"
            /* Pointer events inside the field must not reach the surface: the
               surface's pointerdown treats any click as "clicked away" and
               commits the draft, which tore the field down the moment the user
               clicked into it to edit. */
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
            style={{
              left: textDraftVisual.left,
              top: textDraftVisual.top,
              width: textDraftVisual.contentWidth,
              minWidth: textDraftVisual.minWidth,
              height: textDraftVisual.fontSize * 1.25,
              color,
              caretColor: color,
              fontFamily: ANNOTATION_FONT_FAMILY,
              fontWeight: ANNOTATION_FONT_WEIGHT,
              fontSize: textDraftVisual.fontSize,
              lineHeight: 1,
            }}
            value={textDraft.value}
            /* Single-line only: the renderer rejects embedded newlines rather
               than wrapping, so this must never become a <textarea>. Paste is
               flattened for the same reason. */
            onChange={(event) =>
              setTextDraft((current) =>
                current
                  ? {
                      ...current,
                      value: event.target.value.replace(/[\r\n]+/g, ' '),
                    }
                  : current,
              )
            }
            onKeyDown={(event) => {
              event.stopPropagation()
              if (event.key === 'Enter') {
                event.preventDefault()
                commitTextDraft()
              } else if (event.key === 'Escape') {
                event.preventDefault()
                editorLog({
                  event: 'annotate:escape_ignored',
                  concern: 'text-draft',
                  trace_id: traceId,
                  node_id: nodeId,
                })
              }
            }}
          />
        )}

        {limitReached && (
          <div className="absolute left-1/2 top-3 z-20 -translate-x-1/2 rounded bg-destructive/90 px-2 py-1 text-xxs text-white">
            {t('annotate.limitReached', { max: MAX_ANNOTATIONS })}
          </div>
        )}
        {fontError && (
          <div
            data-testid="annotation-font-error"
            className="absolute left-1/2 top-12 z-20 max-w-lg -translate-x-1/2 rounded-lg border border-destructive bg-background px-3 py-2 text-sm text-destructive shadow-xl"
          >
            {fontError}
          </div>
        )}
      </div>

      <AnnotationToolPalette
        tool={tool}
        onToolChange={(next) => {
          commitTextDraft()
          setTool(next)
          if (next !== 'select') setSelectedId(null)
        }}
        color={color}
        onColorChange={setColor}
        width={widthValue}
        widthMin={widthControlsText ? MIN_FONT_SIZE_PCT : 0.3}
        widthMax={widthControlsText ? MAX_FONT_SIZE_PCT : 6}
        widthStep={widthControlsText ? FONT_SIZE_STEP_PCT : 0.1}
        onWidthChange={handleWidthChange}
        canUndo={canUndo}
        canRedo={canRedo}
        onUndo={handleUndo}
        onRedo={handleRedo}
        canDelete={selectedId !== null}
        onDelete={handleDeleteSelected}
        canSave={elements.length > 0 || (textDraft?.value.trim() ?? '') !== ''}
        isSaving={isSaving}
        onSave={handleSave}
        onCancel={onCancel}
        showCommitActions={!isInline}
        inline={isInline}
      />
    </div>
  )

  // The lightbox portals to the body so it escapes the node card's stacking
  // and overflow contexts. The inline surface must NOT: it belongs to the
  // panel's layout flow.
  return isInline ? body : createPortal(body, document.body)
}))

AnnotationEditorOverlay.displayName = 'AnnotationEditorOverlay'
